<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import DOMPurify from 'dompurify';
import { aiAssistantOpen, aiSettings, saveAiSettings, runAiChat } from '@/helpers/aiAssistant';

const router = useRouter();

const messages = ref([]); // { role: 'user'|'assistant'|'error', content }
const input = ref('');
const busy = ref(false);
const status = ref('');
const scrollRef = ref(null);
const inputRef = ref(null);
const showSettings = ref(false);
const settingsDraft = ref({ ...aiSettings.value });
let abortController = null;

const SUGGESTIONS = [
  'Welche VMs sind gestoppt?',
  'Wie ist der Zustand der Cluster?',
  'Welche VM hat die IP 93.174.',
  'Wo finde ich die IP-Zuweisungen?',
];

// Body-Klasse verschmälert den restlichen Inhalt (siehe unscoped Style).
watch(aiAssistantOpen, async (open) => {
  document.body.classList.toggle('ai-assistant-open', open);
  if (open) {
    await nextTick();
    inputRef.value?.focus();
  }
}, { immediate: true });

const scrollToBottom = async () => {
  await nextTick();
  if (scrollRef.value) scrollRef.value.scrollTop = scrollRef.value.scrollHeight;
};

const send = async (text = input.value) => {
  const question = String(text || '').trim();
  if (!question || busy.value) return;
  input.value = '';
  messages.value.push({ role: 'user', content: question });
  busy.value = true;
  status.value = 'Denke nach …';
  scrollToBottom();
  abortController = new AbortController();
  try {
    const history = messages.value
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .slice(-12)
      .map(({ role, content }) => ({ role, content }));
    const answer = await runAiChat(history, {
      signal: abortController.signal,
      onStatus: (s) => { status.value = s; },
    });
    messages.value.push({ role: 'assistant', content: answer.content, tools: answer.tools, dataNotNeeded: answer.dataNotNeeded });
  } catch (error) {
    if (error?.name === 'AbortError') {
      messages.value.push({ role: 'error', content: 'Abgebrochen.' });
    } else {
      const unreachable = error instanceof TypeError;
      messages.value.push({
        role: 'error',
        content: unreachable
          ? `LM Studio nicht erreichbar unter ${aiSettings.value.endpoint} — läuft der Server (inkl. CORS)? Chrome fragt ggf. nach Zugriff aufs lokale Netzwerk.`
          : (error?.message || 'Unbekannter Fehler.'),
      });
    }
  } finally {
    busy.value = false;
    status.value = '';
    abortController = null;
    scrollToBottom();
    nextTick(() => inputRef.value?.focus());
  }
};

const stop = () => abortController?.abort();
const clearChat = () => { if (!busy.value) messages.value = []; };

const onKeydown = (event) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    send();
  }
};

const applySettings = () => {
  saveAiSettings({
    endpoint: String(settingsDraft.value.endpoint || '').trim() || 'http://localhost:1234/v1',
    model: String(settingsDraft.value.model || '').trim() || 'qwen/qwen3.8-27b',
  });
  showSettings.value = false;
};

// Minimales Markdown: Links (nur interne Pfade), fett, Code, Listen, Tabellen, Absätze.
const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const INTERNAL_PREFIX_RE = /^\/(virtual-infrastructure|administratives|help|support|changelog)(\/|$)/;
const renderMarkdown = (text) => {
  // Modelle erfinden gern eine Domain vor internen Pfaden (z.B. https://example.com/virtual-…).
  const normalized = String(text || '')
    .replace(/\]\(https?:\/\/[^/)\s]+(\/[^)\s]*)\)/g, (all, path) => (INTERNAL_PREFIX_RE.test(path) ? `](${path})` : all))
    .replace(/^\s*>\s?/gm, '');
  let html = escapeHtml(normalized);
  html = html.replace(/\[([^\]]+)\]\((\/(?!\/)[^)\s]*)\)/g, '<a href="$2" class="ai-link">$1</a>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.split(/\n{2,}/).map((block) => {
    const lines = block.split('\n');
    if (lines.every((l) => /^\s*([-*]|\d+\.)\s+/.test(l))) {
      return `<ul>${lines.map((l) => `<li>${l.replace(/^\s*([-*]|\d+\.)\s+/, '')}</li>`).join('')}</ul>`;
    }
    if (lines.every((l) => /^\s*\|.*\|\s*$/.test(l))) {
      const rows = lines.filter((l) => !/^\s*\|[\s:|-]+\|\s*$/.test(l));
      return `<table class="table table-sm mb-2">${rows.map((l, i) => `<tr>${l.trim().slice(1, -1).split('|')
        .map((c) => (i === 0 ? `<th>${c.trim()}</th>` : `<td>${c.trim()}</td>`)).join('')}</tr>`).join('')}</table>`;
    }
    return `<p>${lines.join('<br>')}</p>`;
  }).join('');
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: ['a', 'strong', 'code', 'ul', 'li', 'p', 'br', 'table', 'tr', 'th', 'td'], ALLOWED_ATTR: ['href', 'class'] });
};

const renderedMessages = computed(() => messages.value.map((m) => ({
  ...m,
  html: m.role === 'assistant' ? renderMarkdown(m.content) : '',
})));

// Interne Links im SPA-Router öffnen statt Seite neu zu laden.
const onContentClick = (event) => {
  const link = event.target?.closest?.('a.ai-link');
  if (!link) return;
  const href = link.getAttribute('href') || '';
  if (!href.startsWith('/') || href.startsWith('//')) return;
  event.preventDefault();
  router.push(href).catch(() => {});
};
</script>

<template>
  <aside v-if="aiAssistantOpen" class="ai-assistant-panel shadow-lg" aria-label="KI-Assistent">
    <header class="ai-assistant-header d-flex align-items-center gap-2 px-3 py-2 border-bottom">
      <i class="ri-robot-2-line fs-4 text-primary"></i>
      <div class="flex-grow-1">
        <div class="fw-semibold">KI-Assistent</div>
        <div class="text-muted" style="font-size: 0.7rem;">Experiment · nur lesend · {{ aiSettings.model }}</div>
      </div>
      <button type="button" class="btn btn-sm btn-soft-secondary" title="Einstellungen" @click="showSettings = !showSettings; settingsDraft = { ...aiSettings }">
        <i class="ri-settings-3-line"></i>
      </button>
      <button type="button" class="btn btn-sm btn-soft-secondary" title="Verlauf leeren" :disabled="busy || !messages.length" @click="clearChat">
        <i class="ri-delete-bin-line"></i>
      </button>
      <button type="button" class="btn btn-sm btn-soft-secondary" title="Schliessen" @click="aiAssistantOpen = false">
        <i class="ri-close-line"></i>
      </button>
    </header>

    <div v-if="showSettings" class="border-bottom p-3 small">
      <label class="form-label mb-1" for="ai-endpoint">API-Endpoint (OpenAI-kompatibel)</label>
      <input id="ai-endpoint" v-model="settingsDraft.endpoint" type="text" class="form-control form-control-sm mb-2" placeholder="http://localhost:1234/v1" />
      <label class="form-label mb-1" for="ai-model">Modell</label>
      <input id="ai-model" v-model="settingsDraft.model" type="text" class="form-control form-control-sm mb-2" placeholder="qwen/qwen3.8-27b" />
      <div class="d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-sm btn-light" @click="showSettings = false">Abbrechen</button>
        <button type="button" class="btn btn-sm btn-primary" @click="applySettings">Übernehmen</button>
      </div>
    </div>

    <div ref="scrollRef" class="ai-assistant-messages p-3" @click="onContentClick">
      <div v-if="!messages.length" class="text-muted small">
        <p class="mb-2">Frag mich zu VMs, Netzen, Verzeichnissen, Cluster-Zustand, Abrechnung, Preisen, Kunden — oder wo du eine Seite findest.</p>
        <div class="d-flex flex-column gap-1">
          <button v-for="s in SUGGESTIONS" :key="s" type="button" class="btn btn-sm btn-soft-primary text-start" @click="send(s)">{{ s }}</button>
        </div>
      </div>
      <div v-for="(m, idx) in renderedMessages" :key="idx" class="mb-3" :class="m.role === 'user' ? 'text-end' : ''">
        <div v-if="m.role === 'user'" class="ai-bubble ai-bubble-user d-inline-block text-start">{{ m.content }}</div>
        <!-- eslint-disable-next-line vue/no-v-html — Inhalt ist DOMPurify-sanitized (Whitelist) -->
        <div v-else-if="m.role === 'assistant'" class="ai-bubble ai-bubble-assistant" v-html="m.html"></div>
        <div v-else class="ai-bubble ai-bubble-error"><i class="ri-error-warning-line me-1"></i>{{ m.content }}</div>
        <div v-if="m.role === 'assistant' && Array.isArray(m.tools) && !m.dataNotNeeded" class="ai-sources mt-1" :class="m.tools.length ? 'text-muted' : 'text-warning'">
          <template v-if="m.tools.length"><i class="ri-database-2-line me-1"></i>Daten: {{ m.tools.join(', ') }}</template>
          <template v-else><i class="ri-error-warning-line me-1"></i>Ohne Datenabfrage beantwortet</template>
        </div>
      </div>
      <div v-if="busy" class="text-muted small d-flex align-items-center gap-2">
        <span class="spinner-border spinner-border-sm"></span>{{ status }}
      </div>
    </div>

    <footer class="border-top p-2">
      <div class="d-flex gap-2 align-items-end">
        <textarea
          ref="inputRef" v-model="input" class="form-control form-control-sm" rows="2"
          placeholder="Frage stellen … (Enter = senden, Shift+Enter = Zeilenumbruch)" :disabled="busy" @keydown="onKeydown"
        ></textarea>
        <button v-if="busy" type="button" class="btn btn-sm btn-soft-danger" title="Abbrechen" @click="stop"><i class="ri-stop-circle-line"></i></button>
        <button v-else type="button" class="btn btn-sm btn-primary" title="Senden" :disabled="!input.trim()" @click="send()"><i class="ri-send-plane-2-line"></i></button>
      </div>
      <div class="text-muted mt-1" style="font-size: 0.68rem;">Antworten können falsch sein — Angaben im vManager prüfen.</div>
    </footer>
  </aside>
</template>

<style scoped>
.ai-assistant-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: var(--ai-panel-width, 400px);
  /* Über der Tasks-Leiste der VM-Seite (1040), unter Modals/Hilfe. */
  z-index: 1041;
  display: flex;
  flex-direction: column;
  background: var(--vz-body-bg, #fff);
  border-left: 1px solid var(--vz-border-color);
}

.ai-assistant-header {
  min-height: 70px;
}

.ai-assistant-messages {
  flex: 1 1 auto;
  overflow-y: auto;
}

.ai-bubble {
  padding: 0.5rem 0.75rem;
  border-radius: 0.6rem;
  font-size: 0.85rem;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.ai-bubble-user {
  background: var(--vz-primary-bg-subtle);
  white-space: pre-wrap;
}

.ai-bubble-assistant {
  background: var(--vz-light);
}

.ai-bubble-assistant :deep(p) { margin: 0 0 0.4rem; }
.ai-bubble-assistant :deep(p:last-child) { margin-bottom: 0; }
.ai-bubble-assistant :deep(ul) { margin: 0 0 0.4rem 1rem; padding: 0; }
.ai-bubble-assistant :deep(table) { font-size: 0.78rem; }
.ai-bubble-assistant :deep(a) { font-weight: 600; }

.ai-bubble-error {
  background: var(--vz-danger-bg-subtle);
  color: var(--vz-danger);
}

.ai-sources {
  font-size: 0.68rem;
}
</style>

<style>
/* Inhalt rechts verschmälern, solange der Assistent offen ist (ab Tablet-Breite). */
body { --ai-panel-width: 400px; }
@media (min-width: 992px) {
  body.ai-assistant-open .main-content { margin-right: var(--ai-panel-width); }
  body.ai-assistant-open #page-topbar,
  body.ai-assistant-open .footer { right: var(--ai-panel-width); }
}
@media (max-width: 991.98px) {
  body { --ai-panel-width: 100vw; }
}
</style>
